import { api } from "@/services/api";

export const entityManagementApi = api.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        // Organizations
        getOrgs: builder.query<any, void>({
            query: () => "/api/account/v1/orgs",
            transformResponse: (response: any) => response?.data?.[0] || null,
            providesTags: ["Org"],
        }),

        // Portfolios
        getPortfolios: builder.query<any[], string>({
            query: (orgId) => `/api/account/v1/orgs/${orgId}/portfolios`,
            transformResponse: (response: any) => response?.data || [],
            providesTags: (_result, _error, orgId) => [
                { type: "Portfolio", id: "LIST" },
                { type: "Portfolio", id: orgId },
            ],
        }),

        savePortfolio: builder.mutation<void, { orgId: string; data: { display_name: string } }>({
            query: ({ orgId, data }) => ({
                url: `/api/account/v1/orgs/${orgId}/portfolios`,
                method: "POST",
                body: data,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (_result, _error, { orgId }) => [
                { type: "Portfolio", id: "LIST" },
                { type: "Portfolio", id: orgId },
                "Portfolio",
                "OrgMetadata",
            ],
        }),

        updatePortfolio: builder.mutation<void, { orgId: string; portfolioId: string; data: any }>({
            query: ({ orgId, portfolioId, data }) => ({
                url: `/api/account/v1/orgs/${orgId}/portfolios/${portfolioId}`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, { portfolioId }) => [
                { type: "Portfolio", id: portfolioId },
                "Portfolio",
                "OrgMetadata",
            ],
        }),

        deletePortfolio: builder.mutation<void, { orgId: string; portfolioId: string }>({
            query: ({ orgId, portfolioId }) => ({
                url: `/api/account/v1/orgs/${orgId}/portfolios/${portfolioId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Portfolio", "OrgMetadata"],
        }),

        // Accounts
        getAccountsByPortfolio: builder.query<any[], string>({
            query: (portfolioId) => `/api/account/v1/portfolios/${portfolioId}/venue_accounts`,
            transformResponse: (response: any) => response?.data || [],
            providesTags: (_result, _error, portfolioId) => [
                { type: "Account", id: "LIST" },
                { type: "Account", id: portfolioId },
            ],
        }),

        getAccount: builder.query<any, { portfolioId: string; accountId: string }>({
            query: ({ portfolioId, accountId }) =>
                `/api/account/v1/portfolios/${portfolioId}/venue_accounts/${accountId}`,
            transformResponse: (response: any) => {
                // If response has a data property, unwrap it; otherwise use response directly
                return response?.data || response;
            },
            providesTags: (_result, _error, { accountId }) => [
                { type: "Account", id: accountId },
            ],
        }),

        saveAccount: builder.mutation<void, { portfolioId: string; data: any }>({
            query: ({ portfolioId, data }) => ({
                url: `/api/account/v1/portfolios/${portfolioId}/venue_accounts`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, { portfolioId }) => [
                { type: "Account", id: "LIST" },
                { type: "Account", id: portfolioId },
                "Account",
                "OrgMetadata",
            ],
        }),

        updateAccount: builder.mutation<void, { portfolioId: string; accountId: string; data: any }>({
            query: ({ portfolioId, accountId, data }) => ({
                url: `/api/account/v1/portfolios/${portfolioId}/venue_accounts/${accountId}`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, { portfolioId, accountId }) => [
                { type: "Account", id: accountId },
                { type: "Account", id: "LIST" },
                { type: "Account", id: portfolioId },
                "Account",
                "OrgMetadata",
            ],
        }),

        deleteAccount: builder.mutation<void, { portfolioId: string; accountId: string }>({
            query: ({ portfolioId, accountId }) => ({
                url: `/api/account/v1/portfolios/${portfolioId}/venue_accounts/${accountId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, { portfolioId }) => [
                { type: "Account", id: "LIST" },
                { type: "Account", id: portfolioId },
                "Account",
                "OrgMetadata",
            ],
        }),

        // API Keys
        getAccountApiKeys: builder.query<any[], string>({
            query: (accountId) => `/api/account/v1/venue_accounts/${accountId}/apikeys`,
            transformResponse: (response: any) => {
                // API returns { keys: [...] }
                if (Array.isArray(response)) return response;
                return response?.keys || response?.data || [];
            },
            providesTags: (_result, _error, accountId) => [
                { type: "ApiKey", id: accountId },
                { type: "ApiKey", id: "LIST" },
            ],
        }),

        saveApiKey: builder.mutation<void, { accountId: string; data: any }>({
            query: ({ accountId, data }) => ({
                url: `/api/account/v1/venue_accounts/${accountId}/apikeys`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, { accountId }) => [
                { type: "ApiKey", id: accountId },
                { type: "ApiKey", id: "LIST" },
            ],
        }),

        deleteApiKey: builder.mutation<void, { accountId: string; keyId: string }>({
            query: ({ accountId, keyId }) => ({
                url: `/api/account/v1/venue_accounts/${accountId}/apikeys/${keyId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, { accountId }) => [
                { type: "ApiKey", id: accountId },
                { type: "ApiKey", id: "LIST" },
            ],
        }),

        // Reference Data
        getVenueCodes: builder.query<string[], void>({
            query: () => "/api/refdata/v1/venue_codes",
            transformResponse: (response: any) => {
                // Handle both wrapped ({ data: [...] }) and unwrapped ([...]) responses
                if (Array.isArray(response)) return response;
                return response?.data || [];
            },
            providesTags: ["VenueCodes"],
        }),

        getDropcopyIps: builder.query<any[], void>({
            query: () => "/api/account/v1/ips/dropcopy",
            transformResponse: (response: any) => {
                // Handle both wrapped ({ data: [...] }) and unwrapped ([...]) responses
                if (Array.isArray(response)) return response;
                return response?.data || [];
            },
            providesTags: ["DropcopyIps"],
        }),
    }),
});

export const {
    useGetOrgsQuery,
    useGetPortfoliosQuery,
    useSavePortfolioMutation,
    useUpdatePortfolioMutation,
    useDeletePortfolioMutation,
    useGetAccountsByPortfolioQuery,
    useLazyGetAccountsByPortfolioQuery,
    useGetAccountQuery,
    useSaveAccountMutation,
    useUpdateAccountMutation,
    useDeleteAccountMutation,
    useGetAccountApiKeysQuery,
    useSaveApiKeyMutation,
    useDeleteApiKeyMutation,
    useGetVenueCodesQuery,
    useGetDropcopyIpsQuery,
} = entityManagementApi;
